'use client'

import { useMemo } from 'react'

import { useInfiniteQuery } from '@tanstack/react-query'
import { plainToInstance } from 'class-transformer'

import { CreationDetail, filterExploreList, FilterExploreParams, getExploreList } from '../..'

import { EXPLORE_LIST_KEY, FILTER_EXPLORE_LIST_KEY } from './explore-prefetch'

// 重新导出 query keys 以保持向后兼容
export { EXPLORE_LIST_KEY, FILTER_EXPLORE_LIST_KEY } from './explore-prefetch'

export const useExploreList = (pageSize = 20, enabled = true) => {
  const query = useInfiniteQuery({
    queryKey: [EXPLORE_LIST_KEY, pageSize],
    queryFn: async ({ pageParam = 0 }) => {
      const res = await getExploreList({
        page: pageParam,
        pageSize
      })
      return { ...res, pageSize }
    },
    getNextPageParam: (lastPage, allPages, lastPageParam) => {
      if (!lastPage.list || lastPage.list.length === 0) {
        return undefined
      }
      // 用 total 判断是否还有更多数据
      const loadedCount = allPages.reduce((acc, page) => acc + (page.list?.length || 0), 0)
      if (typeof lastPage.total === 'number' && loadedCount >= lastPage.total) {
        return undefined
      }
      return lastPageParam + 1
    },
    initialPageParam: 0,
    enabled
  })

  // 在 useMemo 中进行类转换，确保无论数据来自服务端预取还是客户端请求都能正确转换
  const list = useMemo(() => {
    if (!query.data) return []
    return query.data.pages.reduce((acc: CreationDetail[], page) => {
      const pageList = page.list || []
      return [...acc, ...pageList.map(item => plainToInstance(CreationDetail, item))]
    }, [])
  }, [query.data])

  const total = useMemo(() => {
    if (!query.data) return 0
    return query.data.pages.reduce((acc, page) => acc + (page.list?.length || 0), 0)
  }, [query.data])

  return {
    list,
    query,
    total
  }
}

export const useFilterExplore = (params: FilterExploreParams = {}) => {
  const query = useInfiniteQuery({
    queryKey: [FILTER_EXPLORE_LIST_KEY, params],
    enabled: !!params.tags,
    refetchOnWindowFocus: false,
    queryFn: async ({ pageParam = 0 }) => {
      const res = await filterExploreList({
        tags: params.tags,
        description: params.description,
        countryCode: params.countryCode,
        page: pageParam,
        pageSize: 30
      })
      return { ...res, pageSize: 30 }
    },
    getNextPageParam: (lastPage, allPages, lastPageParam) => {
      if (!lastPage.list || lastPage.list.length === 0) {
        return undefined
      }
      // 用 total 判断是否还有更多数据
      const loadedCount = allPages.reduce((acc, page) => acc + (page.list?.length || 0), 0)
      if (typeof lastPage.total === 'number' && loadedCount >= lastPage.total) {
        return undefined
      }
      return lastPageParam + 1
    },
    initialPageParam: 0
  })

  // 在 useMemo 中进行类转换，确保无论数据来自服务端预取还是客户端请求都能正确转换
  const list = useMemo(() => {
    if (!query.data) return []
    return query.data.pages.reduce((acc: CreationDetail[], page) => {
      const pageList = page.list || []
      return [...acc, ...pageList.map(item => plainToInstance(CreationDetail, item))]
    }, [])
  }, [query.data])

  return {
    list,
    query
  }
}
